import {
  Move, DoorClosed, Flame, ShieldAlert, Volume2, Keyboard as KeyboardIcon,
  Wind, Droplet, HelpCircle, Wrench,
} from 'lucide-react';
import type { ComponentType } from 'react';
import type { AlarmZoneType } from '@asi/shared';
import { cn } from '@/lib/utils';

// Material-style device icons: a lucide glyph inside a colored circular badge
// — bigger and clearer than a bare small icon. Prefers Hikvision's own
// `detector_type` (ground truth from ISAPI sync); falls back to zone-name
// keywords for zones never synced yet.

type IconComponent = ComponentType<{ className?: string }>;

const DETECTOR_TYPE_ICON_RULES: { pattern: RegExp; Icon: IconComponent; color: string }[] = [
  { pattern: /passiveinfrared|pir|curtain|dualtech|motion|shock/i, Icon: Move, color: 'bg-amber-500/10 text-amber-600' },
  { pattern: /magnetic|contact|door|window/i, Icon: DoorClosed, color: 'bg-blue-500/10 text-blue-600' },
  { pattern: /smoke|heat/i, Icon: Flame, color: 'bg-orange-500/10 text-orange-600' },
  { pattern: /panic|emergency|duress/i, Icon: ShieldAlert, color: 'bg-purple-500/10 text-purple-600' },
  { pattern: /siren|sound/i, Icon: Volume2, color: 'bg-red-500/10 text-red-600' },
  { pattern: /keypad|keyboard/i, Icon: KeyboardIcon, color: 'bg-slate-500/10 text-slate-600' },
  { pattern: /gas|combustible|\bco\b/i, Icon: Wind, color: 'bg-yellow-500/10 text-yellow-600' },
  { pattern: /water|flood/i, Icon: Droplet, color: 'bg-cyan-500/10 text-cyan-600' },
  { pattern: /repeater|expander/i, Icon: Wrench, color: 'bg-gray-500/10 text-gray-500' },
];

/** Renders the device icon for a zone as a colored circular badge (Material-style, bigger than a bare icon). */
export function DeviceIconBadge({ detectorType, name, zoneType, className }: {
  detectorType: string | null; name: string; zoneType: AlarmZoneType; className?: string;
}) {
  const source = detectorType ?? name;
  const hit = DETECTOR_TYPE_ICON_RULES.find((r) => r.pattern.test(source));
  let Icon: IconComponent = HelpCircle;
  let color = 'bg-gray-500/10 text-gray-500';
  if (hit) {
    Icon = hit.Icon;
    color = hit.color;
  } else if (zoneType === 'PANIC') { Icon = ShieldAlert; color = 'bg-purple-500/10 text-purple-600'; }
  else if (zoneType === 'FIRE') { Icon = Flame; color = 'bg-orange-500/10 text-orange-600'; }
  else if (zoneType === 'PERIMETER') { Icon = DoorClosed; color = 'bg-blue-500/10 text-blue-600'; }
  else if (zoneType === 'INTERIOR') { Icon = Move; color = 'bg-amber-500/10 text-amber-600'; }

  return (
    <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0', color, className)}>
      <Icon className="w-4 h-4" />
    </div>
  );
}

/** Spanish labels for Hikvision's `detectorType` ISAPI field. Falls back to a
 * humanized version of the raw value for types not yet catalogued here. */
const DETECTOR_TYPE_LABELS_ES: Record<string, string> = {
  passiveInfraredDetector: 'Sensor de movimiento (PIR)',
  curtainDetector: 'Sensor de movimiento (cortina)',
  dualTechDetector: 'Sensor de movimiento (dual PIR+microondas)',
  magneticContact: 'Contacto magnético',
  smokeDetector: 'Detector de humo',
  combustibleGasDetector: 'Detector de gas',
  gasDetector: 'Detector de gas',
  coDetector: 'Detector de monóxido de carbono',
  waterDetector: 'Detector de agua/inundación',
  floodDetector: 'Detector de agua/inundación',
  glassBreakDetector: 'Detector de rotura de vidrio',
  emergencyButton: 'Botón de pánico',
  panicButton: 'Botón de pánico',
  wirelessSiren: 'Sirena inalámbrica',
  indoorSiren: 'Sirena interior',
  outdoorSiren: 'Sirena exterior',
  repeater: 'Repetidor de señal',
  keypad: 'Teclado',
  remoteController: 'Control remoto',
  keyfob: 'Llavero de control',
  shockDetector: 'Sensor de vibración/golpe',
  temperatureHumiditySensor: 'Sensor de temperatura/humedad',
  wiredZoneExpander: 'Expansor de zona cableada',
};

/** Translates a raw Hikvision `detectorType` to Spanish; humanizes unknown values as a fallback. */
export function translateDetectorType(detectorType: string): string {
  if (DETECTOR_TYPE_LABELS_ES[detectorType]) return DETECTOR_TYPE_LABELS_ES[detectorType];
  return detectorType.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

